// ============================================================
// CONDITION SYSTEM - Pure functions, zero React dependency
// ============================================================
import { rollDiceExpression } from './dice';
import { parseExposure, describeExposure, exposureGoodForBearer } from './exposures';
import { parseOverwatch } from './overwatch';
import { parseHallucination, describeHallucination, mergeHallucination } from './hallucinationCode';
import { drainUnitLabel } from './statDrains';
import { stackPolicyOf, type StackPolicy } from './stackPolicy';
import { splitClock } from './codeClock';

export const COND_NAMES: Record<string, string> = {
  S: 'Stunned',
  B: 'Blinded',
  D: 'Deafened',
  P: 'Prone',
  F: 'Fatigue',
  BL: 'Blood Loss',
  BLS: 'Severe Blood Loss',
  Fi: 'On Fire',
  U: 'Unconscious',
  Cr: 'Crippled',
  De: 'Destroyed',
  Dy: 'Dying',
  Pa: 'Paralysed',
  NS: 'Cannot Speak',
  K: 'Dead',
  Dr: 'Drop Weapon',
  Hw: 'Haywire',
  // Hallucinating (v8.357.0) - an INTENSITY 1-10, never a vision:
  // `Hl7` steps down a round, `Hl7:3` holds for three. No modifier.
  Hl: 'Hallucinating',
  // Snared (v8.198.2) - Immobilised by a weapon, escapable by the
  // per-turn Agility test (turnEffects.ts). A GM-SET Immobilised stays
  // a ruling with no escape roll; that is the whole reason for two codes.
  Sn: 'Snared',
  // Regeneration halted by Suppressing(X) (v8.286.0) - the flesh will
  // not knit while the sanctic burn lasts.
  Rg: 'Regen Halted',
  // Guard beaten aside by Disarming(X) (v8.287.0) - parry suspended
  // while the count runs; the weapon is wrenched, not dropped.
  Dsm: 'Disarmed',
  // Pinned (v8.198.4) - rules-spec.md's suppression state: −20 BS,
  // crawl-only movement, shaken off by a per-turn Willpower test.
  Pn: 'Pinned',
  // Prepared states (v8.214.0) - self-applied stances the engine
  // consumes or expires by EVENT, never by the round countdown, which
  // is why none of them join DURATION_CONDS. See engine/preparedStates.
  Ah: 'Aiming (Half)',
  Af: 'Aiming (Full)',
  Ft: 'Feint Advantage',
  Ds: 'Defensive Stance',
  Gd: 'Guarded',
  Ao: 'All-Out',
  Ov: 'Overwatch',
  // Movement states (2026-10-26, plan cover-cue-and-objects): Ran - he moved at a Run, -20 to shoot him, +20
  // to strike him in melee, full auto only for his own shots; Tactical Advance - cover to cover, counts as in
  // cover while he moves; Disengaged - he broke off from melee this turn. Event-expired like the stances.
  Rn: 'Ran',
  Ta: 'Tactical Advance',
  Dg: 'Disengaged',
  // Unaware and Helpless (v8.440.0) - what the ATTACKER gets against
  // them, not something they roll with. See engine/unaware, engine/helpless.
  Unw: 'Unaware',
  Hpl: 'Helpless',
  // Vehicle-specific condition codes
  Immob: 'Immobilised',
  WepDest: 'Weapon Destroyed',
  EngDmg: 'Engine Damaged',
  HullBr: 'Hull Breach',
  CrewHit: 'Crew Hit',
  Disabled: 'System Disabled',
  Explodes: 'Explodes',
};

/** Full-name labels for custom-modifier scope keys (characteristics + 'all'). */
const PEN_SCOPE_LABELS: Record<string, string> = {
  all: 'All tests',
  // Not a test at all: metres on the map. Its own scope rather than
  // Agility, because "−10 to movement" says the man is slower, not that
  // he is clumsier - see engine/conditionMovement.ts.
  movement: 'Movement',
  ws: 'Weapon Skill',
  bs: 'Ballistic Skill',
  str: 'Strength',
  tgh: 'Toughness',
  agi: 'Agility',
  int: 'Intelligence',
  per: 'Perception',
  wp: 'Willpower',
  fel: 'Fellowship',
};

/** Human label for a custom-modifier scope key (skill ids are title-cased). */
export function customPenScopeLabel(scope: string): string {
  return PEN_SCOPE_LABELS[scope] || scope.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/**
 * Parse a STRUCTURED custom-modifier token
 * `Pen:<scope>[+<scope>…]=<value>[:<rounds>]` - 'Pen:fel=-10',
 * 'Pen:dodge=-10:3', 'Pen:dodge+movement=-20:5'. Returns null for legacy
 * free-text `Pen:` tokens (which stay display-only). `value` is signed;
 * `rounds` 0 = permanent.
 *
 * SEVERAL SCOPES, ONE PENALTY. "−20 to Dodge and Movement for 1d10
 * rounds" is a single effect with a single duration, and writing it as
 * two codes would roll that duration twice - leaving a man with seven
 * rounds of one and four of the other. `scopes` is therefore a list, and
 * every consumer applies the same value to each of them.
 */
export interface CustomPen {
  scopes: string[];
  value: number;
  rounds: number;
  /** A clamp for `raise` stacking (v8.367.0): the total never passes it (in the value's own direction). */
  cap?: number;
}

/** `Pen:<scope>[+<scope>…]=<value>[^<cap>][:<rounds>]` */
export function parseCustomPen(code: string): CustomPen | null {
  if (!code.startsWith('Pen:')) return null;
  const clock = splitClock(code.slice(4));
  const body = clock ? clock.body : code.slice(4);
  const rounds = clock ? clock.rounds : 0;
  const m = body.match(/^([a-z_]+(?:\+[a-z_]+)*)=(-?\d+)(?:\^(-?\d+))?$/);
  if (!m) return null;
  const out: CustomPen = { scopes: m[1].split('+'), value: parseInt(m[2], 10), rounds };
  if (m[3] !== undefined) out.cap = parseInt(m[3], 10);
  return out;
}

/** The inverse of `parseCustomPen`. */
export function buildCustomPen(pen: CustomPen): string {
  let code = 'Pen:' + pen.scopes.join('+') + '=' + pen.value;
  if (pen.cap !== undefined) code += '^' + pen.cap;
  if (pen.rounds > 0) code += ':' + pen.rounds;
  return code;
}

/**
 * Haywire's five official rows (v8.198.6). The stored number IS the
 * severity index - the countdown's one-per-round decrement is exactly
 * the field "lessening one step in severity each Round".
 */
const HAYWIRE_SEVERITIES = ['Minor Disruption', 'Major Disruption', 'Dead Zone', 'Total Shutdown'] as const;

export function haywireSeverityName(n: number): string {
  if (n <= 0) return 'Insignificant';
  return HAYWIRE_SEVERITIES[Math.min(n, HAYWIRE_SEVERITIES.length) - 1];
}

/** "3 rounds" / "1 round". */
const roundsLabel = (n: number): string => n + (n === 1 ? ' round' : ' rounds');

/** A legacy free-text penalty token (display-only): its text, and its rounds when it has them. */
function decodeLegacyPen(c: string): string {
  const rest = c.slice(4);
  const clock = splitClock(rest);
  return clock ? clock.body + ' (' + roundsLabel(clock.rounds) + ')' : rest;
}

/** Structured custom modifier → "Fellowship −10", "Dodge +10 (3 rounds)"; else the legacy text. */
function decodePen(c: string): string {
  const pen = parseCustomPen(c);
  if (!pen) return decodeLegacyPen(c);
  const sign = pen.value >= 0 ? '+' : '−';
  const base = pen.scopes.map(customPenScopeLabel).join(' & ') + ' ' + sign + Math.abs(pen.value);
  return pen.rounds > 0 ? base + ' (' + roundsLabel(pen.rounds) + ')' : base;
}

/** Dn: stat drains (v8.290.0) → "Agility drained 5 (3 rounds)". */
function decodeDrain(c: string): string {
  const m = c.match(/^Dn:(\w+)=(\d+)(?::(\d+))?$/);
  if (!m) return c;
  // In the units the drain was TYPED in, named: "Toughness bonus
  // −2 (3 rounds)". The modifier card shows the same drain in test
  // points (−20); the unit word is what keeps the two legible
  // together (v8.296.0).
  const base = drainUnitLabel(m[1]) + ' \u2212' + m[2];
  const rounds = m[3] ? parseInt(m[3], 10) : 0;
  return rounds > 0 ? base + ' (' + roundsLabel(rounds) + ')' : base;
}

/** The code families that carry their own grammar after a prefix. */
const PREFIX_DECODERS: readonly [string, (c: string) => string][] = [
  ['Pen:', decodePen],
  // Ex: exposures (v8.349.0) → "Painted (3 rounds, hostiles)".
  ['Ex:', (c) => describeExposure(c) ?? c],
  ['Dn:', decodeDrain],
];

/** Overwatch, a located crit, a hallucination, Haywire or bare Dying - null when it is none of them. */
function decodeSpecial(c: string): string | null {
  // Overwatch (v8.308.0) → "Overwatch (90°, full)".
  const ov = parseOverwatch(c);
  if (ov) return 'Overwatch (' + ov.heading + '\u00B0, ' + ov.mode + ')';
  const locMatch = c.match(/^(Cr|De):(.+)$/);
  if (locMatch) return (COND_NAMES[locMatch[1]] || locMatch[1]) + ' (' + locMatch[2] + ')';
  // Hallucinating (v8.357.0) → 'Hallucinating 7 (severe, 3 rounds)'.
  const hl = c.startsWith('Hl') ? describeHallucination(c) : null;
  if (hl) return hl;
  // Haywire renders its severity NAME, not a round count - the number is
  // a ladder index (v8.198.6).
  const hwMatch = c.match(/^Hw(\d+)$/);
  if (hwMatch) return 'Haywire: ' + haywireSeverityName(parseInt(hwMatch[1], 10));
  // Bare Dy = no death clock - the threat is the LACK of treatment, not time.
  // (Timed variants render as "Dying (N rounds)" below; countdown never ticks this one.)
  return c === 'Dy' ? 'Dying (untreated)' : null;
}

/** A catalog code with a count: "Stunned (3 rounds)", "Fatigue (1 level)", "Stunned (1d5 rounds)". */
function decodeTimed(name: string, letters: string, dur: string): string {
  const unit = letters === 'F' ? 'level' : 'round';
  if (dur.includes('d')) return name + ' (' + dur + ' ' + unit + 's)';
  const n = parseInt(dur, 10);
  return name + ' (' + n + ' ' + unit + (n === 1 ? '' : 's') + ')';
}

/** A catalog code: its name, with its count or its trailing number; anything unknown comes back as typed. */
function decodeCatalog(c: string): string {
  if (c.includes(' ') && c.length > 5) return c;
  if (COND_NAMES[c]) return COND_NAMES[c];
  const m = c.match(/^([A-Z][a-z]*)(\d.*)$/);
  if (m && COND_NAMES[m[1]]) return decodeTimed(COND_NAMES[m[1]], m[1], m[2]);
  const m2 = c.match(/^([A-Z]{2,3})(\d*)$/);
  if (m2 && COND_NAMES[m2[1]]) return COND_NAMES[m2[1]] + (m2[2] ? ' ' + m2[2] : '');
  return c;
}

/** A condition code as the words a chip shows. Empty codes come back unchanged. */
export function decodeCond(c: string | null | undefined): string | null | undefined {
  if (!c) return c;
  const prefixed = PREFIX_DECODERS.find(([prefix]) => c.startsWith(prefix));
  if (prefixed) return prefixed[1](c);
  return decodeSpecial(c) ?? decodeCatalog(c);
}

interface CondColorResult {
  bg: string;
  border: string;
  text: string;
}

type CondColorName = 'yellow' | 'orange' | 'red' | 'green' | 'black';

const COND_COLORS: Record<CondColorName, CondColorResult> = {
  yellow: { bg: '#3a3515', border: '#78681d', text: '#fde68a' },
  orange: { bg: '#3a2515', border: '#9a3412', text: '#fdba74' },
  red: { bg: '#3a1515', border: '#7f1d1d', text: '#fca5a5' },
  green: { bg: '#14321a', border: '#166534', text: '#86efac' },
  black: { bg: '#1a1a1a', border: '#555', text: '#fff' },
};

/** The conditions that threaten life itself - red chips, and the first to sort after Dead. */
const DIRE_CONDITIONS = new Set(['Dy', 'BL', 'BLS', 'U', 'Explodes']);
const isDire = (ct: string): boolean => DIRE_CONDITIONS.has(ct) || ct.startsWith('De:');

/** Timed amber, scene-long orange. */
const clockColor = (code: string): CondColorName => (condDuration(code) > 0 ? 'yellow' : 'orange');

/** The code families whose colour depends on the code's own contents. */
const FAMILY_COLORS: readonly [string, (code: string) => CondColorName][] = [
  // Structured benefit (+value) reads as green; penalties/legacy keep the amber family.
  ['Pen:', (code) => ((parseCustomPen(code)?.value ?? 0) > 0 ? 'green' : clockColor(code))],
  // Exposures: read from the BEARER since 2026-10-23 - green when good for
  // whoever wears it, amber when bad (`exposureGoodForBearer`).
  [
    'Ex:',
    (code) => {
      const ex = parseExposure(code);
      return ex && exposureGoodForBearer(ex) ? 'green' : 'yellow';
    },
  ],
  // Stat drains (v8.293.0): always a penalty - timed amber, scene-long orange.
  ['Dn:', clockColor],
];

/**
 * The colour of each plain condition type. Prepared stances are boons;
 * All-Out is a stance you PAY for, so it reads as a penalty. Anything not
 * listed (Stunned, Blinded, the vehicle damage, a crippled limb...) is orange.
 */
const TYPE_COLORS = new Map<string, CondColorName>([
  ['K', 'black'],
  ...(['Ah', 'Af', 'Ft', 'Ds', 'Gd', 'Ov', 'Ta', 'Dg'].map((ct) => [ct, 'green']) as [string, CondColorName][]),
  ['Rn', 'yellow'],
  ['Ao', 'red'],
  ...(['F', 'P', 'Dr'].map((ct) => [ct, 'yellow']) as [string, CondColorName][]),
]);

/** Which of the chip colours a condition code wears. */
function condColorName(code: string): CondColorName {
  const ct = condType(code);
  const family = FAMILY_COLORS.find(([prefix]) => ct.startsWith(prefix));
  if (family) return family[1](code);
  return TYPE_COLORS.get(ct) ?? (isDire(ct) ? 'red' : 'orange');
}

/** The chip colours for a condition code - a fresh object each call, as callers may style it further. */
export function condColor(code: string): CondColorResult {
  return { ...COND_COLORS[condColorName(code)] };
}

export function resolveCondCode(rawCode: string, narr?: (msg: string) => void): string {
  const m = rawCode.match(/^([A-Z][a-z]*(?::[^0-9]*)?)(\d+d\d+.*)$/);
  if (!m) return rawCode;
  const prefix = m[1],
    diceExpr = m[2];
  const roll = rollDiceExpression(diceExpr);
  const resolved = prefix + roll.total;
  const name = COND_NAMES[prefix] || COND_NAMES[prefix.split(':')[0]] || prefix;
  if (narr) narr('\uD83C\uDFB2 ' + name + ': rolled ' + diceExpr + ' \u2192 ' + roll.total + ' rounds');
  return resolved;
}

interface CritDiceResult {
  text: string;
  fx: string;
  rolls: { expr: string; value: number }[];
}

/** The dice expressions in a crit line, in order; none for an empty line. */
const diceIn = (s: string): string[] => (s && s.match(/\d*d\d+/g)) || [];

/** Replace each dice expression in `s`, in order, with the value `valueOf` gives it (by position). */
function substituteDice(s: string, valueOf: (expr: string, index: number) => number): string {
  let index = 0;
  return s.replace(/\d*d\d+/g, (expr) => String(valueOf(expr, index++)));
}

/**
 * Roll the dice in a critical effect's text and its fx code. The fx dice
 * roll FIRST, in order, and the text's dice reuse them position by
 * position (the text describes the same effect); only text dice beyond the
 * fx's count roll fresh. That order is the dice order E2E seeds against.
 */
export function rollCritDice(text: string, fx: string): CritDiceResult {
  if (diceIn(fx).length === 0 && diceIn(text).length === 0) return { text, fx, rolls: [] };
  const fxValues = diceIn(fx).map((d) => rollDiceExpression(d).total);
  const resolvedFx = substituteDice(fx || '', (_, i) => fxValues[i]);
  const rolls: { expr: string; value: number }[] = [];
  const resolvedText = substituteDice(text || '', (expr, i) => {
    const value = i < fxValues.length ? fxValues[i] : rollDiceExpression(expr).total;
    rolls.push({ expr, value });
    return value;
  });
  return { text: resolvedText, fx: resolvedFx, rolls };
}

/**
 * What a MERGE keys on (v8.367.0) - as `condType`, except that a custom
 * modifier is its scopes alone (the value is what the policy decides) and
 * a preset exposure is its name. `condType` keeps the value, because the
 * chips, the readers and `buildCondCode` all want the whole code back.
 */
export function condIdentity(code: string): string {
  const pen = parseCustomPen(code);
  if (pen) return 'Pen:' + pen.scopes.join('+');
  if (code.startsWith('Ex:')) {
    const name = code.slice(3).split(';')[0];
    if (name && name !== 'custom') return 'Ex:' + name;
  }
  return condType(code);
}

/**
 * The families whose code ends in an optional `:<rounds>` clock -
 * custom modifiers, stat drains (v8.290.0) and exposures (v8.349.0).
 */
const TIMED_PREFIXES = ['Pen:', 'Dn:', 'Ex:'] as const;

/** The timed family a code (or a condition type) belongs to, if any. */
const timedPrefixOf = (code: string) => TIMED_PREFIXES.find((prefix) => code.startsWith(prefix));

/** True for a custom modifier, stat drain or exposure code. */
export function isTimedFamily(code: string): boolean {
  return timedPrefixOf(code) !== undefined;
}

export function condType(code: string): string {
  const prefix = timedPrefixOf(code);
  if (prefix) {
    const clock = splitClock(code.slice(prefix.length));
    return clock ? prefix + clock.body : code;
  }
  // Overwatch (v8.308.0): the parameters are the ZONE, not an instance,
  // so every Ov: code is the same condition - which is what makes
  // mergeCondition keep exactly one kill zone per combatant.
  if (code.startsWith('Ov:')) return 'Ov';
  if (/^(Cr|De):(.+)$/.test(code)) return code;
  const m = code.match(/^([A-Z][a-z]*)[\d]/);
  return m ? m[1] : code;
}

/** Does this condition list hold a condition of type `code` (Ds, P, K...)? The one check; the hooks re-export it. */
export function hasCondition(conds: readonly string[] | null | undefined, code: string): boolean {
  return (conds ?? []).some((c) => condType(c) === code);
}

/**
 * Conditions that take a combatant out of the turn order (v8.72.0).
 *
 * NARROWER than `conditionMovement.ts`'s `STOPS`, deliberately. That
 * list answers "can they move", and Stunned and Immobilised belong on
 * it - but a stunned combatant still has a turn, they merely have a bad
 * one. This answers "is there a turn to take at all".
 *
 * Dying counts: it is unconscious with a clock attached. Paralysed does
 * NOT, though it arguably could - it is temporary, and burning the turn
 * is often exactly what a table wants to watch happen.
 */
const DOWNED_CONDITIONS: readonly string[] = ['K', 'U', 'Dy'];

/** Is this combatant out of the fight - dead, unconscious or dying? */
export function isDowned(conditions: readonly string[] | null | undefined): boolean {
  if (!conditions) return false;
  return conditions.some((c) => DOWNED_CONDITIONS.includes(condType(c)));
}

export function condDuration(code: string): number {
  // Hallucinating (v8.357.0): the number after the LETTERS is the
  // intensity, not a clock - the clock is the HOLD's `:rounds`, and a
  // STEP has none (it decays by its own rule in the countdown).
  if (code.startsWith('Hl')) return parseHallucination(code)?.rounds ?? 0;
  if (isTimedFamily(code)) return splitClock(code.slice(code.indexOf(':') + 1))?.rounds ?? 0;
  const m = code.match(/^[A-Z][a-z]*(\d+)$/);
  return m ? parseInt(m[1], 10) : 0;
}

export const DURATION_CONDS: Set<string> = new Set(['S', 'B', 'D', 'F', 'Dy', 'Hw']);

export function isDurationCond(code: string): boolean {
  const ct = condType(code);
  if (DURATION_CONDS.has(ct)) return condDuration(code) > 0;
  if (ct.startsWith('Pen:') || ct.startsWith('Ex:')) return condDuration(code) > 0;
  return false;
}

export function buildCondCode(type: string, duration: number): string {
  if (isTimedFamily(type)) return duration > 0 ? type + ':' + duration : type;
  return type + (duration > 0 ? duration : '');
}

/**
 * Apply `code` to a condition list that may already hold it (v8.367.0).
 *
 * `policy` is a StackPolicy - or the old boolean, read as additive →
 * extend, else refresh - and decides what "already there" means:
 * refresh (stronger value, longer clock), extend (stronger value, clocks
 * add), raise (values add, clamped to a cap, longer clock) or stack (keep
 * both). "Already there" is `condIdentity`: a custom modifier's SCOPES,
 * a preset exposure's name, else the condition type - so Movement +10
 * and Movement +20 are the same thing to merge, and the policy says how.
 * Haywire never sums; a hallucination has its own merge.
 */
export function mergeCondition(newConds: string[], code: string, policy: StackPolicy | boolean): void {
  const rule = stackPolicyOf(policy);
  const ct = condType(code);
  // ONE hallucination per person (v8.357.0): the generic path would
  // stack a second `Hl` beside the first. A code arriving here has no
  // profile to say how it stacks, so it REFRESHES - the higher
  // intensity, the new clock; a weapon's profile decides in
  // processHallucinogens, which never comes this way.
  if (ct === 'Hl') return refreshHallucination(newConds, code);
  if (!makeRoom(newConds, ct)) return;
  const pen = parseCustomPen(code);
  if (!isMergeable(code, ct, pen) || rule === 'stack') {
    if (!newConds.includes(code)) newConds.push(code);
    return;
  }
  mergeByIdentity(newConds, code, pen, rule);
}

/** Merge a bare hallucination code by refreshing the one already worn. */
function refreshHallucination(newConds: string[], code: string): void {
  const h = parseHallucination(code);
  if (h) mergeHallucination(newConds, h, 'refresh');
}

/**
 * Clear the way for a newcomer of type `ct`: remove what it displaces.
 * False when it adds nothing - blood loss on a man already losing it
 * severely.
 */
function makeRoom(newConds: string[], ct: string): boolean {
  if (REFUSED_BESIDE[ct] && newConds.some((c) => condType(c) === REFUSED_BESIDE[ct])) return false;
  const displaced = DISPLACES.get(ct);
  if (displaced) removeWhere(newConds, (c) => condType(c) === displaced);
  return true;
}

/**
 * A newcomer that adds nothing beside what is already there: blood loss
 * on a man already losing it severely, and Dying on the dead (the user's
 * ruling, 2026-09-28 - found by ./dev fuzz: the picker allowed both).
 */
const REFUSED_BESIDE: Record<string, string> = { BL: 'BLS', Dy: 'K' };

/**
 * The pairs where the newcomer takes the other's place: severe blood loss
 * replaces blood loss, and one aim at a time - taking the fuller aim
 * replaces the lesser, and vice versa.
 */
const DISPLACES = new Map([
  ['BLS', 'BL'],
  // Death is final (the user's ruling, 2026-09-28): Dead replaces Dying.
  ['K', 'Dy'],
  ['Ah', 'Af'],
  ['Af', 'Ah'],
]);

/** Remove, in place, every entry the predicate picks. */
function removeWhere(list: string[], picks: (c: string) => boolean): void {
  for (let i = list.length - 1; i >= 0; i--) {
    if (picks(list[i])) list.splice(i, 1);
  }
}

/** True when a code merges with its kind: a custom modifier, a catalog clock, or a timed Pen:/Ex:. */
function isMergeable(code: string, ct: string, pen: CustomPen | null): boolean {
  if (pen || DURATION_CONDS.has(ct)) return true;
  return (ct.startsWith('Pen:') || ct.startsWith('Ex:')) && condDuration(code) > 0;
}

/** Replace every worn code with the same `condIdentity` by the one the policy merges them into. */
function mergeByIdentity(newConds: string[], code: string, pen: CustomPen | null, rule: StackPolicy): void {
  const id = condIdentity(code);
  const same = newConds.filter((c) => condIdentity(c) === id);
  if (same.length === 0) {
    newConds.push(code);
    return;
  }
  const merged = pen ? mergePens(same, pen, rule) : mergeClocks(same, code, condType(code), rule);
  removeWhere(newConds, (c) => condIdentity(c) === id);
  newConds.push(merged);
}

/** The longer of two clocks, where 0 (permanent) is the longest there is. */
const longerClock = (a: number, b: number) => (a === 0 || b === 0 ? 0 : Math.max(a, b));
const addedClock = (a: number, b: number) => (a === 0 || b === 0 ? 0 : a + b);

/** The value of greater magnitude; the first on a tie. */
const stronger = (a: number, b: number) => (Math.abs(b) > Math.abs(a) ? b : a);

/** The tightest of the caps, in the value's own direction; undefined when there are none. */
function tightestCap(caps: number[]): number | undefined {
  return caps.length ? caps.reduce((a, b) => (Math.abs(b) < Math.abs(a) ? b : a)) : undefined;
}

/** A total held to its cap, in the value's own direction. */
function clampToCap(total: number, cap: number | undefined): number {
  return cap === undefined || Math.abs(total) <= Math.abs(cap) ? total : cap;
}

/** A custom modifier merged with the ones already worn on the same scopes. */
function mergePens(same: string[], incoming: CustomPen, rule: StackPolicy): string {
  const worn = same.map(parseCustomPen).filter((p): p is CustomPen => !!p);
  const wornClock = worn.reduce((acc, p) => longerClock(acc, p.rounds), worn[0]?.rounds ?? 0);
  const cap = tightestCap([...worn.map((p) => p.cap), incoming.cap].filter((c): c is number => c !== undefined));
  let value: number;
  let rounds: number;
  if (rule === 'raise') {
    value = clampToCap(worn.reduce((acc, p) => acc + p.value, 0) + incoming.value, cap);
    rounds = longerClock(wornClock, incoming.rounds);
  } else {
    value = stronger(
      worn.reduce((acc, p) => stronger(acc, p.value), 0),
      incoming.value,
    );
    rounds = (rule === 'extend' ? addedClock : longerClock)(wornClock, incoming.rounds);
  }
  return buildCustomPen({ scopes: incoming.scopes, value, rounds, ...(cap !== undefined ? { cap } : {}) });
}

/** A catalog clock (Stunned 3) or a timed exposure merged by its clock alone. */
function mergeClocks(same: string[], code: string, ct: string, rule: StackPolicy): string {
  const dur = condDuration(code);
  let bestIdx = 0;
  let bestDur = condDuration(same[0]);
  for (let i = 1; i < same.length; i++) {
    const d = condDuration(same[i]);
    if (d > bestDur) {
      bestIdx = i;
      bestDur = d;
    }
  }
  // Haywire NEVER sums (v8.198.6): overlapping fields do not stack -
  // official wording keeps the higher and discards the lower.
  const adds = (rule === 'extend' || rule === 'raise') && ct !== 'Hw';
  if (adds) return buildCondCode(condType(code), bestDur + dur);
  return dur > bestDur ? code : same[bestIdx];
}

// Map non-location conditions to logical body parts
export const COND_BODY_MAP: Record<string, string[]> = {
  B: ['head'],
  D: ['head'],
  NS: ['head'],
  Immobilised: ['rightLeg', 'leftLeg'],
  Immob: ['rightLeg', 'leftLeg'],
  Sn: ['rightLeg', 'leftLeg'],
  Pa: ['rightLeg', 'leftLeg'],
};

/** Conditions that impair without threatening life - the second rank of the sort. */
const IMPAIRING_CONDITIONS = new Set([
  'S',
  'B',
  'D',
  'NS',
  'Pa',
  'Fi',
  'Sn',
  'Immob',
  'WepDest',
  'EngDmg',
  'HullBr',
  'CrewHit',
  'Disabled',
]);

/** True for the second rank: an impairment, a crippled limb, or a permanent custom modifier. */
function isImpairing(ct: string, code: string): boolean {
  if (IMPAIRING_CONDITIONS.has(ct) || ct.startsWith('Cr:') || code === 'Immobilised') return true;
  return ct.startsWith('Pen:') && condDuration(code) === 0;
}

/** Sort rank: 0 dead, 1 life-threatening, 2 impairing, 3 everything else. */
export function condSeverityOrder(code: string): number {
  const ct = condType(code);
  if (ct === 'K') return 0;
  if (isDire(ct)) return 1;
  return isImpairing(ct, code) ? 2 : 3;
}

interface CondIconPath {
  d: string;
  bg?: boolean;
  stroke?: boolean;
}

// SVG icon definitions for condition types (16x16 coordinate space)
export const COND_ICONS: Record<string, CondIconPath[]> = {
  // Exposures (v8.349.0): a reticle - what OTHERS see when they look at them.
  Ex: [
    { d: 'M8,2A6,6 0 1,1 8,14A6,6 0 1,1 8,2Z M8,4.5A3.5,3.5 0 1,0 8,11.5A3.5,3.5 0 1,0 8,4.5Z' },
    { d: 'M8,0L8,3 M8,13L8,16 M0,8L3,8 M13,8L16,8 M8,6.5A1.5,1.5 0 1,1 8,9.5A1.5,1.5 0 1,1 8,6.5Z', stroke: true },
  ],
  // Prepared states (v8.214.0)
  Ah: [
    { d: 'M8,2.5A5.5,5.5 0 1,1 8,13.5A5.5,5.5 0 1,1 8,2.5Z M8,4.5A3.5,3.5 0 1,0 8,11.5A3.5,3.5 0 1,0 8,4.5Z' },
    { d: 'M8,0.5L8,3.5 M8,12.5L8,15.5 M0.5,8L3.5,8 M12.5,8L15.5,8', stroke: true },
  ],
  Af: [
    { d: 'M8,2.5A5.5,5.5 0 1,1 8,13.5A5.5,5.5 0 1,1 8,2.5Z M8,4.5A3.5,3.5 0 1,0 8,11.5A3.5,3.5 0 1,0 8,4.5Z' },
    { d: 'M8,6.5A1.5,1.5 0 1,1 8,9.5A1.5,1.5 0 1,1 8,6.5Z' },
    { d: 'M8,0.5L8,3.5 M8,12.5L8,15.5 M0.5,8L3.5,8 M12.5,8L15.5,8', stroke: true },
  ],
  Ft: [{ d: 'M3,2L12,11L11,13L9.5,12.5L2,5L3,2Z' }, { d: 'M13,2L4,11L5,13L6.5,12.5L14,5L13,2Z', bg: true }],
  Ds: [
    { d: 'M8,1L14,3.5L14,8C14,12 11.5,14.5 8,15.5C4.5,14.5 2,12 2,8L2,3.5L8,1Z' },
    { d: 'M8,4L11.5,5.5L11.5,8C11.5,10.5 10,12.2 8,13C6,12.2 4.5,10.5 4.5,8L4.5,5.5L8,4Z', bg: true },
  ],
  // Ran: a chevron doubled - he went at a run. Tactical Advance: a figure bent behind a wall. Disengaged: an
  // arrow breaking out of a circle.
  Rn: [{ d: 'M2,3L7,8L2,13L4,13L9,8L4,3Z' }, { d: 'M7,3L12,8L7,13L9,13L14,8L9,3Z' }],
  Ta: [{ d: 'M2,10L14,10L14,14L2,14Z' }, { d: 'M6,9L6,5A2,2 0 1,1 10,5L10,9Z', bg: true }],
  Dg: [
    { d: 'M7,3A5,5 0 1,0 7,13', stroke: true },
    { d: 'M7,8L15,8 M12,5L15,8L12,11', stroke: true },
  ],
  // Overwatch: a reticle with a wedge cut out of it - the arc you cover.
  Ov: [
    { d: 'M8,1.5A6.5,6.5 0 1,1 8,14.5A6.5,6.5 0 1,1 8,1.5Z M8,3.5A4.5,4.5 0 1,0 8,12.5A4.5,4.5 0 1,0 8,3.5Z' },
    { d: 'M8,8L14,4.5L14,11.5Z' },
    { d: 'M8,6.75A1.25,1.25 0 1,1 8,9.25A1.25,1.25 0 1,1 8,6.75Z' },
  ],
  // All-Out: two blades crossed and no shield anywhere - everything
  // committed forward, nothing held back to defend with.
  Ao: [
    { d: 'M2.5,2.5L13.5,13.5', stroke: true },
    { d: 'M13.5,2.5L2.5,13.5', stroke: true },
    { d: 'M6.5,6.5A1.5,1.5 0 1,1 6.5,9.5A1.5,1.5 0 1,1 6.5,6.5Z' },
  ],
  // Guarded: the Defensive Stance shield with a blade laid across it -
  // you are still swinging, just carefully. A code with NO entry here
  // renders nothing at all (ConditionIcon returns null), which is the
  // trap the Dn drains fell into in v8.293.0.
  Gd: [
    { d: 'M5,2L11,4.5L11,8.5C11,11.5 9,13.5 8,14C7,13.5 5,11.5 5,8.5L5,4.5L5,2Z' },
    { d: 'M2.5,13.5L13.5,3.5', stroke: true },
  ],
  K: [
    {
      d: 'M8,1C4.5,1 2,3.5 2,7C2,10.5 4,12 6,12L6,15 7,13.5 8,15 9,13.5 10,15 10,12C12,12 14,10.5 14,7C14,3.5 11.5,1 8,1Z',
    },
    { d: 'M5,5.5A1.3,1.5 0 1,1 5,8.5A1.3,1.5 0 1,1 5,5.5Z', bg: true },
    { d: 'M11,5.5A1.3,1.5 0 1,1 11,8.5A1.3,1.5 0 1,1 11,5.5Z', bg: true },
    { d: 'M6,10.5L7,10 8,10.5 9,10 10,10.5', stroke: true },
  ],
  Fi: [
    { d: 'M8,1C6,4 3.5,6 3.5,9.5C3.5,12.5 5.5,15 8,15C10.5,15 12.5,12.5 12.5,9.5C12.5,6 10,4 8,1Z' },
    { d: 'M8,7C7,9 6.5,10 7,12C7.5,13 8.5,13 9,12C9.5,11 9,9 8,7Z', bg: true },
  ],
  B: [
    { d: 'M1,8C1,8 4,3.5 8,3.5C12,3.5 15,8 15,8C15,8 12,12.5 8,12.5C4,12.5 1,8 1,8Z' },
    { d: 'M8,5.5A2.5,2.5 0 1,1 8,10.5A2.5,2.5 0 1,1 8,5.5Z', bg: true },
    { d: 'M3,13L13,3', stroke: true },
  ],
  D: [
    { d: 'M10,2C13,4 14,7 13,10C12,13 10,14 8,14C6,14 5,12 6,10C7,8 9,7 10,6', stroke: true },
    { d: 'M4,2L12,14', stroke: true },
  ],
  S: [{ d: 'M8,1L9.8,5.8L15,6.2L11,9.8L12.4,15L8,12L3.6,15L5,9.8L1,6.2L6.2,5.8Z' }],
  P: [
    { d: 'M3,8.5A2.5,2.5 0 1,1 3,13.5A2.5,2.5 0 1,1 3,8.5Z' },
    { d: 'M6,11L15,11', stroke: true },
    { d: 'M12,8L15,11L12,14', stroke: true },
  ],
  F: [{ d: 'M4,2L3,6L4,7L5,6Z' }, { d: 'M8,5L7,9L8,10L9,9Z' }, { d: 'M12,8L11,12L12,13L13,12Z' }],
  BL: [{ d: 'M8,2C8,2 3,8 3,11C3,13.8 5.2,15 8,15C10.8,15 13,13.8 13,11C13,8 8,2 8,2Z' }],
  BLS: [
    { d: 'M5,3C5,3 1.5,7.5 1.5,10C1.5,12 3,13 5,13C7,13 8.5,12 8.5,10C8.5,7.5 5,3 5,3Z' },
    { d: 'M11,3C11,3 7.5,7.5 7.5,10C7.5,12 9,13 11,13C13,13 14.5,12 14.5,10C14.5,7.5 11,3 11,3Z' },
  ],
  Cr: [
    { d: 'M4,2L7,7', stroke: true },
    { d: 'M5,8L8,14', stroke: true },
    { d: 'M12,2L9,7', stroke: true },
    { d: 'M11,8L8,14', stroke: true },
  ],
  De: [
    { d: 'M8,1.5A6.5,6.5 0 1,1 8,14.5A6.5,6.5 0 1,1 8,1.5Z M8,4A4,4 0 1,0 8,12A4,4 0 1,0 8,4Z' },
    { d: 'M4,4L12,12M12,4L4,12', stroke: true },
  ],
  Dy: [{ d: 'M0,10L3,10L4.5,4L6,13L7.5,7L9,10L16,10', stroke: true }],
  U: [
    { d: 'M3,10L7,10L3,14L7,14', stroke: true },
    { d: 'M6,6L9,6L6,9.5L9,9.5', stroke: true },
    { d: 'M9,3L11.5,3L9,5.5L11.5,5.5', stroke: true },
  ],
  Pa: [{ d: 'M10,0L5,7L8,7L3,16L8,9L5,9Z' }],
  // Pinned (v8.199.13): a crossed-out gun - suppressed, snap fire only.
  // Pistol silhouette (barrel, muzzle notch, grip) with a strike through.
  Pn: [
    { d: 'M2,5L14,5L14,6.5L13,6.5L13,8L8,8L8,9L6.5,12.5L3.5,12.5L5.5,8L2,8Z' },
    { d: 'M2.5,13.5L13.5,2.5', stroke: true },
  ],
  // Snared (v8.199.9): a diagonal net weave, distinct from Immobilised's bars
  Sn: [
    { d: 'M3,5L11,13', stroke: true },
    { d: 'M5,3L13,11', stroke: true },
    { d: 'M13,5L5,13', stroke: true },
    { d: 'M11,3L3,11', stroke: true },
  ],
  Immobilised: [
    { d: 'M5,2L5,14', stroke: true },
    { d: 'M11,2L11,14', stroke: true },
    { d: 'M5,5L11,5', stroke: true },
    { d: 'M5,8L11,8', stroke: true },
    { d: 'M5,11L11,11', stroke: true },
  ],
  NS: [
    { d: 'M8,5C5,5 3,6.5 3,8.5C3,10.5 5,12 8,12C11,12 13,10.5 13,8.5C13,6.5 11,5 8,5Z' },
    {
      d: 'M8,7C6.5,7 5.5,7.5 5.5,8.5C5.5,9.5 6.5,10 8,10C9.5,10 10.5,9.5 10.5,8.5C10.5,7.5 9.5,7 8,7Z',
      bg: true,
    },
    { d: 'M4,4L12,13M12,4L4,13', stroke: true },
  ],
  Dr: [{ d: 'M7.5,1L8.5,1L8.5,10L7.5,10Z' }, { d: 'M5,4L11,4L11,5L5,5Z' }, { d: 'M4,12L8,16L12,12', stroke: true }],
  Pen: [
    { d: 'M8,1L1,14L15,14Z', stroke: true },
    { d: 'M7.5,6L8.5,6L8.5,10L7.5,10Z' },
    { d: 'M8,12A0.8,0.8 0 1,1 8,13.6A0.8,0.8 0 1,1 8,12Z' },
  ],
  // Stat drain (v8.293.0): a droplet bleeding downward - without an
  // entry here ConditionIcon returns null and the drain is INVISIBLE
  // in every icon strip, which the first playtest reported as "the
  // condition never appears".
  Dn: [
    { d: 'M8,1C6.5,4 4.5,6 4.5,8.5C4.5,11 6,12.5 8,12.5C10,12.5 11.5,11 11.5,8.5C11.5,6 9.5,4 8,1Z' },
    { d: 'M6,14L8,16L10,14', stroke: true },
  ],
  Hw: [{ d: 'M10,1L4,9L8,9L6,15L12,7L8,7Z' }],
  // Hallucinating (v8.357.0): a spiral - the world turning on itself.
  Hl: [
    {
      d: 'M8,8 m0,-1 a1,1 0 1,1 -1,1 a2,2 0 1,1 2,-2 a3,3 0 1,1 -3,3 a4,4 0 1,1 4,-4 a5,5 0 1,1 -5,5 a6,6 0 1,1 6,-6',
      stroke: true,
    },
  ],
  // Vehicle-specific condition icons (16x16 coordinate space)
  Immob: [
    { d: 'M5,2L5,14', stroke: true },
    { d: 'M11,2L11,14', stroke: true },
    { d: 'M5,5L11,5', stroke: true },
    { d: 'M5,8L11,8', stroke: true },
    { d: 'M5,11L11,11', stroke: true },
  ],
  WepDest: [
    { d: 'M2,10L8,10L14,6', stroke: true },
    { d: 'M5,4L11,12M11,4L5,12', stroke: true },
  ],
  EngDmg: [
    { d: 'M8,3A5,5 0 1,1 8,13A5,5 0 1,1 8,3Z' },
    { d: 'M8,5.5A2.5,2.5 0 1,0 8,10.5A2.5,2.5 0 1,0 8,5.5Z', bg: true },
    { d: 'M6,3L10,13', stroke: true },
  ],
  HullBr: [{ d: 'M8,1L2,4L2,9C2,12 5,15 8,15C11,15 14,12 14,9L14,4Z' }, { d: 'M6,6L10,10M10,6L6,10', stroke: true }],
  CrewHit: [
    { d: 'M8,4A2,2 0 1,1 8,8A2,2 0 1,1 8,4Z' },
    { d: 'M5,9L11,9L12,15L8,12L4,15Z' },
    { d: 'M12,3L14,2L13,4L15,4L13,5L14,7L12,5.5L10,7L11,5L9,4L11,4Z' },
  ],
  Disabled: [
    { d: 'M8,2A6,6 0 1,1 8,14A6,6 0 1,1 8,2Z' },
    { d: 'M8,5A3,3 0 1,0 8,11A3,3 0 1,0 8,5Z', bg: true },
    { d: 'M3,8L13,8', stroke: true },
  ],
  Explodes: [{ d: 'M8,1L9,5L13,3L10,7L15,8L10,9L13,13L9,11L8,15L7,11L3,13L6,9L1,8L6,7L3,3L7,5Z' }],
};

/**
 * Will `ConditionIcon` actually draw this code?
 *
 * It returns `null` when `COND_ICONS` has no entry, which is invisible
 * in a list that flows - but the battlefield lays its condition chips
 * out at FIXED offsets, so a code that draws nothing leaves a hole in
 * the row rather than closing up. The marker picks what it can render.
 *
 * This is deliberately the same lookup the component does, and the two
 * must stay in step: a chip chosen here and dropped there is the gap.
 */
export function hasConditionIcon(code: string): boolean {
  const ct = condType(code);
  const iconKey = ct.includes(':') ? ct.split(':')[0] : ct;
  return !!(COND_ICONS[iconKey] || COND_ICONS[COND_NAMES[iconKey]]);
}
